// The planning change log: who changed what, since the advisor handed off.
//
// Entries are produced by diffing the form at each successful Salesforce save
// against the form at the previous one (see StoreContext), stamped with the
// planner's identity and the save time, and appended here. Append-only,
// oldest first.
//
// Kept pure so the coalescing rules — the part that decides what story the
// log tells — can be unit-tested without a browser.

// A planner reshaping one field across several autosaves is ONE story, not
// five. Consecutive 'changed' entries for the same field by the same person
// within this window collapse into a single entry that keeps the ORIGINAL
// before-value and the LATEST after-value — the log reads "moved Market Value
// from 50,000 to 75,000", not four intermediate keystroke states.
const COALESCE_MS = 15 * 60 * 1000;

// The log rides one Salesforce Long Text Area, so it cannot grow without
// bound. The real limit is CHARACTERS, not entries: a 500-entry log of
// ordinary edits measures ~145,000 characters, which overflows the field's
// 131,072 maximum — and an over-length value does not truncate quietly on
// this field type, it fails the save outright, exactly as the 255-character
// notes cap did to a pilot advisor on 8/13. So the cap is a serialized-size
// budget with headroom, and the entry count is only a cheap first guard.
//
// When either is hit the OLDEST entries drop, because "what changed recently"
// is the question the log answers, and the full drift from handoff stays
// computable regardless by diffing the live record against the snapshot.
const MAX_ENTRIES = 500;
const MAX_CHARS = 120000;

// Drop oldest entries until the serialized log fits the budget.
function fitToBudget(entries) {
  let out = entries.length > MAX_ENTRIES ? entries.slice(-MAX_ENTRIES) : entries;
  while (out.length > 1 && JSON.stringify(out).length > MAX_CHARS) {
    // Drop a proportional chunk rather than one at a time: a log this size is
    // thousands of characters per iteration otherwise.
    const over = JSON.stringify(out).length - MAX_CHARS;
    const perEntry = Math.max(1, Math.floor(JSON.stringify(out).length / out.length));
    out = out.slice(Math.max(1, Math.ceil(over / perEntry)));
  }
  return out;
}

const sameField = (a, b) =>
  a.area === b.area && a.rowUid === b.rowUid && a.field === b.field;

export function appendLogEntries(log, entries, who, atIso) {
  const base = Array.isArray(log) ? [...log] : [];
  const at = Date.parse(atIso) || 0;

  for (const e of entries) {
    const stamped = { t: atIso, who, ...e };

    if (e.kind === 'changed') {
      // Find the most recent entry telling this same field's story.
      let i = -1;
      for (let j = base.length - 1; j >= 0; j--) {
        if (base[j].kind === 'changed' && base[j].who === who && sameField(base[j], e)) {
          i = j;
          break;
        }
      }
      if (i !== -1 && at - (Date.parse(base[i].t) || 0) < COALESCE_MS) {
        const merged = { ...stamped, before: base[i].before };
        base.splice(i, 1);
        // Typed back to where it started: the two edits cancel and the log
        // carries nothing, rather than a "changed X to X" entry.
        if (merged.before === merged.after) continue;
        base.push(merged);
        continue;
      }
    }

    base.push(stamped);
  }

  return fitToBudget(base);
}

// One JSON string for the Salesforce field, '' when there is nothing to say.
export function serializeLog(log) {
  if (!Array.isArray(log) || log.length === 0) return '';
  try {
    return JSON.stringify(log);
  } catch {
    return '';
  }
}

// The reverse. Malformed content reads as no log rather than blocking the
// load of the record it rides on.
export function parseLog(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return null;
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) return null;
    const entries = parsed.filter(e => e && typeof e === 'object' && e.t);
    return entries.length ? fitToBudget(entries) : null;
  } catch {
    return null;
  }
}
